import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Search, Package, Truck, Shield, MapPin, Phone, Mail, Clock,
  ChevronRight, ChevronDown, Check, Facebook, Instagram, MessageCircle,
  Star, ArrowRight, Menu, X, DollarSign, HelpCircle, Zap, Plane, Ship,
  PackageSearch, CheckCircle2, Warehouse, PackageCheck, Home as HomeIcon
} from 'lucide-react';

// Mock tenant website data
const tenantData = {
  companyName: 'Bolt Courier Jamaica',
  tagline: 'Fast & Reliable Courier Services',
  heroTitle: 'Reliable Shipping from USA to Jamaica',
  heroSubtitle: 'Air and ocean freight with real-time tracking, streamlined customs processing, and dependable delivery you can trust.',
  primaryColor: '#facc15',
  secondaryColor: '#3b82f6',
  darkBg: '#050C1A',
  phone: '+1 (876) 555-0123',
  email: 'info@boltcourier.com',
  address: '123 Harbour Street, Kingston, Jamaica',
  hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM',
  whatsapp: '+18765550123',
};

const services = [
  { icon: Zap, title: 'Fast Processing', desc: 'Optimized workflows and priority handling reduce delays and speed up delivery.', color: '#facc15' },
  { icon: Search, title: 'Live Tracking', desc: 'Track your shipment at every stage with real-time status updates via our portal.', color: '#3b82f6' },
  { icon: Shield, title: 'Secure Handling', desc: 'Every package is handled with care, with insurance options available for peace of mind.', color: '#10b981' },
  { icon: Truck, title: 'Island-Wide Delivery', desc: 'Strong logistics links across Jamaica, supported by key distribution hubs.', color: '#8b5cf6' },
];

const airPricingTiers = [
  { weight: '1 lb', jmd: 1200, usd: 7.27 },
  { weight: '2 lbs', jmd: 1450, usd: 8.79 },
  { weight: '3 lbs', jmd: 1950, usd: 11.82 },
  { weight: '4 lbs', jmd: 2450, usd: 14.85 },
  { weight: '5 lbs', jmd: 2950, usd: 17.88 },
];

const oceanPricingTiers = [
  { weight: '1 lb', jmd: 750, usd: 4.55 },
  { weight: '2 lbs', jmd: 1150, usd: 6.97 },
  { weight: '3 lbs', jmd: 1550, usd: 9.39 },
  { weight: '4 lbs', jmd: 1950, usd: 11.82 },
  { weight: '5 lbs', jmd: 2350, usd: 14.24 },
];

const serviceAreas = [
  'Kingston', 'Montego Bay', 'Ocho Rios', 'Portmore', 'Spanish Town', 
  'Mandeville', 'May Pen', 'Negril', 'Falmouth', 'Port Antonio'
];

const faqs = [
  { q: 'How do I get my USA shipping address?', a: 'Once you sign up for a free account, you will instantly receive your unique suite number and our warehouse address in Miami, Florida.' },
  { q: 'How long does air freight take?', a: 'Air freight typically takes 2-4 business days from the time it arrives at our Miami warehouse to being ready for pickup in Jamaica.' },
  { q: 'Do you handle customs clearance?', a: 'Yes, we handle all customs documentation and processing. You simply provide the invoice, and we take care of the rest.' },
  { q: 'Can I ship large appliances via ocean?', a: 'Absolutely. Our ocean freight service is designed specifically for heavy and bulky items at much lower rates than air freight.' },
  { q: 'Is there a weight limit for packages?', a: 'There is no strict weight limit. However, items over 100lbs are generally more cost-effective when shipped via ocean freight.' },
];

const trackingStatuses = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'received', label: 'Received', icon: Warehouse },
  { key: 'in_transit', label: 'In Transit', icon: Plane },
  { key: 'received_jamaica', label: 'Arrived JA', icon: PackageCheck },
  { key: 'ready_for_delivery', label: 'Ready', icon: Package },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

// Progress Timeline Component
const ProgressTimeline = ({ currentStatus }) => {
  const currentIndex = trackingStatuses.findIndex(s => s.key === currentStatus);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;

  return (
    <div className="bolt-progress-timeline">
      <div className="timeline-track">
        <div 
          className="timeline-progress" 
          style={{ width: `${(safeIndex / (trackingStatuses.length - 1)) * 100}%` }}
        />
      </div>
      <div className="timeline-steps">
        {trackingStatuses.map((status, index) => {
          const Icon = status.icon;
          const isCompleted = index <= safeIndex;
          const isCurrent = index === safeIndex;
          return (
            <div 
              key={status.key} 
              className={`timeline-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
            >
              <div className="step-marker">
                <Icon size={16} />
              </div>
              <span className="step-label">{status.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function TenantWebsitePreview() {
  const [activePage, setActivePage] = useState('home');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [trackingResult, setTrackingResult] = useState(null);
  const [rateMode, setRateMode] = useState('air');
  const [weight, setWeight] = useState('');

  const handleTrack = (e) => {
    e.preventDefault();
    if (trackingNumber) {
      setTrackingResult({
        number: trackingNumber.toUpperCase(),
        status: 'in_transit',
        statusText: 'In Transit to Jamaica',
        origin: 'Miami, FL',
        destination: 'Kingston, Jamaica',
        eta: 'Dec 18, 2025',
        carrier: 'Air Freight',
        weight: '12.5 lbs',
        events: [
          { status: 'Package received at Miami warehouse', date: 'Dec 14, 10:30 AM', location: 'Miami, FL', completed: true },
          { status: 'Consolidated for shipping', date: 'Dec 15, 2:00 PM', location: 'Miami, FL', completed: true },
          { status: 'Departed Miami - In Transit', date: 'Dec 16, 8:00 AM', location: 'In Transit', completed: true, current: true },
          { status: 'Arrived in Jamaica', date: 'Pending', location: 'Kingston', completed: false },
          { status: 'Customs clearance', date: 'Pending', location: 'Kingston', completed: false },
          { status: 'Ready for pickup', date: 'Pending', location: 'Kingston', completed: false },
        ]
      });
      setActivePage('track');
    }
  };

  const calculateRate = () => {
    const w = parseFloat(weight) || 0;
    if (w <= 0) return { jmd: 0, usd: 0 };
    
    const billable = Math.ceil(w);
    if (rateMode === 'air') {
      let jmd = 1200;
      if (billable >= 2) jmd += 250;
      if (billable >= 3) jmd += (billable - 2) * 500;
      return { jmd, usd: (jmd / 165).toFixed(2), billable };
    } else {
      const rates = { 1: 750, 2: 1150, 3: 1550, 4: 1950, 5: 2350, 6: 2850, 7: 3150, 8: 3550, 9: 3950, 10: 4350 };
      let jmd = billable <= 10 ? rates[billable] : 4350 + (billable - 10) * 400;
      return { jmd, usd: (jmd / 165).toFixed(2), billable };
    }
  };

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'track', label: 'Track' },
    { id: 'rates', label: 'Rates' },
    { id: 'services', label: 'Services' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="bolt-website" data-testid="tenant-website-preview">
      {/* Navigation */}
      <nav className="bolt-nav">
        <div className="bolt-nav-container">
          <div className="bolt-logo" onClick={() => setActivePage('home')}>
            <div className="logo-icon">
              <Zap size={20} />
            </div>
            <span>{tenantData.companyName}</span>
          </div>
          
          <div className={`bolt-nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            {navItems.map(item => (
              <button 
                key={item.id}
                className={`nav-link ${activePage === item.id ? 'active' : ''}`}
                onClick={() => { setActivePage(item.id); setMobileMenuOpen(false); }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="bolt-nav-actions">
            <button className="sign-in-btn" onClick={() => setActivePage('track')}>
              Sign In
            </button>
            <button 
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Home Page */}
      {activePage === 'home' && (
        <>
          {/* Hero Section */}
          <section className="bolt-hero">
            <div className="hero-bg-effects">
              <div className="hero-glow hero-glow-1"></div>
              <div className="hero-glow hero-glow-2"></div>
              <div className="hero-grid-pattern"></div>
            </div>
            
            <div className="bolt-container hero-content">
              <div className="hero-text">
                <div className="hero-badge">
                  <span className="badge-dot"></span>
                  USA & China to Jamaica Logistics
                </div>
                
                <h1>
                  {tenantData.heroTitle.split('USA').map((part, i) => 
                    i === 0 ? part : <><span className="text-gradient">USA</span>{part}</>
                  )}
                </h1>
                
                <p>{tenantData.heroSubtitle}</p>
                
                <form className="hero-tracking-form" onSubmit={handleTrack}>
                  <div className="tracking-input-wrapper">
                    <div className="tracking-glow"></div>
                    <div className="tracking-input-container">
                      <PackageSearch size={20} />
                      <input 
                        type="text"
                        placeholder="Enter Bolt tracking number..."
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                      />
                      <button type="submit" className="track-btn">
                        Track <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </form>

                <div className="hero-links">
                  <button className="link-btn" onClick={() => setActivePage('rates')}>
                    View Rates
                  </button>
                </div>

                <div className="hero-features">
                  <div className="feature-item">
                    <CheckCircle2 size={16} /> Transparent Pricing
                  </div>
                  <div className="feature-item">
                    <CheckCircle2 size={16} /> Real-Time Updates
                  </div>
                  <div className="feature-item">
                    <CheckCircle2 size={16} /> Insured Shipments
                  </div>
                </div>
              </div>

              <div className="hero-visual">
                <div className="visual-bg-icon">
                  <Zap size={400} />
                </div>
                <div className="floating-icon floating-plane">
                  <Plane size={80} />
                </div>
                <div className="floating-icon floating-ship">
                  <Ship size={100} />
                </div>
                <div className="floating-icon floating-package">
                  <div className="package-card">
                    <PackageSearch size={32} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="bolt-features">
            <div className="bolt-container">
              <div className="section-header">
                <h2>Built for Speed. Designed for Trust.</h2>
                <p>We combine modern technology with logistics expertise to deliver a premium courier experience for Jamaica.</p>
              </div>
              
              <div className="features-grid">
                {services.map((service, i) => (
                  <div key={i} className="feature-card">
                    <div className="feature-icon" style={{ color: service.color }}>
                      <service.icon size={32} />
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Global Network Section */}
          <section className="bolt-network">
            <div className="network-glow"></div>
            <div className="bolt-container">
              <div className="section-header centered">
                <div className="section-badge">
                  <span className="badge-dot blue"></span>
                  Global Network
                </div>
                <h2>Connected Where It Matters</h2>
                <p>Strategic hubs ensuring seamless flow from <span className="highlight">USA & China</span> directly to <span className="highlight yellow">Jamaica</span>.</p>
              </div>

              <div className="network-cards">
                <div className="network-card">
                  <div className="card-badge blue">MIA</div>
                  <h4>Miami</h4>
                  <p>Primary aggregation hub for US shipments and rapid air cargo consolidation.</p>
                </div>
                <div className="network-card">
                  <div className="card-badge red">CN</div>
                  <h4>China</h4>
                  <p>Direct logistics from major Asian manufacturing hubs to Jamaica.</p>
                </div>
                <div className="network-card highlight">
                  <div className="card-badge yellow">KIN</div>
                  <h4>Kingston</h4>
                  <p>Central distribution facility with on-site customs clearing and local dispatch.</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bolt-cta">
            <div className="bolt-container">
              <h2>Ready to Ship with Confidence?</h2>
              <p>Join individuals and businesses across Jamaica who trust us for fast, transparent, and reliable shipping.</p>
              <div className="cta-buttons">
                <button className="btn-primary" onClick={() => setActivePage('track')}>
                  Create Your Account & Start Shipping
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Track Page */}
      {activePage === 'track' && (
        <section className="bolt-track-page">
          <div className="track-header">
            <div className="bolt-container">
              <h1>Track Your Shipment</h1>
              <p>{trackingNumber ? `Tracking: ${trackingNumber.toUpperCase()}` : 'Enter your Bolt tracking number to see real-time updates'}</p>
            </div>
          </div>

          <div className="bolt-container track-content">
            <div className="track-search-card">
              <form className="track-form" onSubmit={handleTrack}>
                <div className="search-input">
                  <PackageSearch size={20} />
                  <input 
                    type="text"
                    placeholder="Enter Bolt tracking number..."
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn-primary">
                  Track
                </button>
              </form>
            </div>

            {trackingResult && (
              <div className="tracking-result">
                <div className="result-card">
                  <div className="result-header">
                    <div className="result-title">
                      <h2>Shipment Status</h2>
                      <span className={`status-badge ${trackingResult.status}`}>
                        {trackingResult.statusText}
                      </span>
                    </div>
                  </div>

                  <ProgressTimeline currentStatus={trackingResult.status} />
                </div>

                <div className="result-details-grid">
                  <div className="detail-card">
                    <div className="detail-icon blue">
                      <MapPin size={24} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Current Location</span>
                      <span className="detail-value">In Transit</span>
                    </div>
                  </div>
                  <div className="detail-card">
                    <div className="detail-icon green">
                      <Clock size={24} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Estimated Delivery</span>
                      <span className="detail-value">{trackingResult.eta}</span>
                    </div>
                  </div>
                  <div className="detail-card">
                    <div className="detail-icon orange">
                      <Plane size={24} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Origin</span>
                      <span className="detail-value">{trackingResult.origin}</span>
                    </div>
                  </div>
                  <div className="detail-card">
                    <div className="detail-icon purple">
                      <MapPin size={24} />
                    </div>
                    <div className="detail-content">
                      <span className="detail-label">Destination</span>
                      <span className="detail-value">{trackingResult.destination}</span>
                    </div>
                  </div>
                </div>

                <div className="result-card">
                  <h3>Tracking History</h3>
                  <div className="events-timeline">
                    {trackingResult.events.map((event, index) => (
                      <div 
                        key={index} 
                        className={`event-item ${event.completed ? 'completed' : ''} ${event.current ? 'current' : ''}`}
                      >
                        <div className="event-marker">
                          {event.completed ? <Check size={14} /> : <div className="marker-dot"></div>}
                        </div>
                        <div className="event-content">
                          <span className="event-status">{event.status}</span>
                          <div className="event-meta">
                            <span><MapPin size={12} /> {event.location}</span>
                            <span>{event.date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Rates Page */}
      {activePage === 'rates' && (
        <section className="bolt-rates-page">
          <div className="rates-header">
            <div className="bolt-container">
              <h1>Transparent Pricing</h1>
              <p>No hidden fees. No surprises. Use our calculator to estimate your shipping costs before you buy.</p>
            </div>
          </div>

          <div className="bolt-container rates-content">
            <div className="rates-grid">
              <div className="calculator-card">
                <div className="calculator-header">
                  <div className="calc-title">
                    <div className="calc-icon">
                      <DollarSign size={20} />
                    </div>
                    <h3>Shipping Calculator</h3>
                  </div>
                  <div className="mode-toggle">
                    <button 
                      className={rateMode === 'air' ? 'active' : ''}
                      onClick={() => setRateMode('air')}
                    >
                      <Plane size={16} /> Air
                    </button>
                    <button 
                      className={rateMode === 'ocean' ? 'active' : ''}
                      onClick={() => setRateMode('ocean')}
                    >
                      <Ship size={16} /> Ocean
                    </button>
                  </div>
                </div>

                <div className="calculator-body">
                  <div className="calc-inputs">
                    <div className="input-group">
                      <label>Package Weight (lbs)</label>
                      <input 
                        type="number"
                        placeholder="Enter weight"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        min="0.1"
                        step="0.1"
                      />
                      {weight && (
                        <span className="input-hint">Billable: {calculateRate().billable} lb</span>
                      )}
                    </div>

                    <div className="pricing-rules">
                      <div className="rule-icon">
                        <HelpCircle size={16} />
                      </div>
                      <div className="rule-content">
                        <strong>Pricing Rules:</strong>
                        {rateMode === 'air' ? (
                          <ul>
                            <li>1 lb: J$1,200</li>
                            <li>2 lbs: J$1,450</li>
                            <li>Each additional lb: +J$500</li>
                          </ul>
                        ) : (
                          <ul>
                            <li>Small packages: By weight</li>
                            <li>$2.50 USD processing fee</li>
                            <li>Delivery: 3-6 weeks</li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="calc-result">
                    <div className="result-bg-icon">
                      {rateMode === 'air' ? <Plane size={80} /> : <Ship size={80} />}
                    </div>
                    <div className="result-badge">
                      {rateMode === 'air' ? 'AIR FREIGHT' : 'OCEAN FREIGHT'}
                    </div>
                    <span className="result-label">Estimated Cost</span>
                    <div className="result-amount">
                      <span className="currency">$</span>
                      <span className="value">{calculateRate().usd || '0.00'}</span>
                    </div>
                    <div className="result-jmd">
                      J${(calculateRate().jmd || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rates-sidebar">
                <div className="rate-table-card">
                  <h3>
                    {rateMode === 'air' ? <><Plane size={18} /> Air Freight Rates</> : <><Ship size={18} /> Ocean Freight Rates</>}
                  </h3>
                  <table className="rate-table">
                    <thead>
                      <tr>
                        <th>Weight</th>
                        <th>USD</th>
                        <th>JMD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rateMode === 'air' ? airPricingTiers : oceanPricingTiers).map((tier, i) => (
                        <tr key={i}>
                          <td>{tier.weight}</td>
                          <td>${tier.usd}</td>
                          <td>{tier.jmd.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="china-card">
                  <h3>China Shipping?</h3>
                  <p>We offer direct logistics from Guangzhou and Shenzhen. Contact us for rates and transit times.</p>
                  <button className="link-btn" onClick={() => setActivePage('contact')}>
                    Contact Us <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services Page */}
      {activePage === 'services' && (
        <section className="bolt-services-page">
          <div className="services-header">
            <div className="bolt-container">
              <h1>Our Services</h1>
              <p>Comprehensive shipping solutions tailored to your needs</p>
            </div>
          </div>

          <div className="bolt-container">
            <div className="services-detailed">
              {services.map((service, i) => (
                <div key={i} className="service-detail-card">
                  <div className="service-icon-large" style={{ background: `${service.color}20`, color: service.color }}>
                    <service.icon size={40} />
                  </div>
                  <div className="service-info">
                    <h3>{service.title}</h3>
                    <p>{service.desc}</p>
                    <ul className="service-features">
                      <li><Check size={14} /> Real-time tracking</li>
                      <li><Check size={14} /> Insurance included</li>
                      <li><Check size={14} /> Customer support</li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="service-areas-section">
              <h2>Service Areas</h2>
              <p>We deliver across Jamaica</p>
              <div className="areas-grid">
                {serviceAreas.map((area, i) => (
                  <div key={i} className="area-tag">
                    <MapPin size={14} /> {area}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Page */}
      {activePage === 'faq' && (
        <section className="bolt-faq-page">
          <div className="faq-header">
            <div className="bolt-container">
              <h1>Frequently Asked Questions</h1>
              <p>Find answers to common questions</p>
            </div>
          </div>

          <div className="bolt-container">
            <div className="faq-list">
              {faqs.map((faq, i) => (
                <div 
                  key={i} 
                  className={`faq-item ${expandedFaq === i ? 'expanded' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                >
                  <div className="faq-question">
                    <span>{faq.q}</span>
                    <ChevronDown size={20} className="faq-icon" />
                  </div>
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Page */}
      {activePage === 'contact' && (
        <section className="bolt-contact-page">
          <div className="contact-header">
            <div className="bolt-container">
              <h1>Contact Us</h1>
              <p>Get in touch with our team</p>
            </div>
          </div>

          <div className="bolt-container">
            <div className="contact-grid">
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <p>{tenantData.phone}</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4>Email</h4>
                    <p>{tenantData.email}</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4>Address</h4>
                    <p>{tenantData.address}</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h4>Business Hours</h4>
                    <p>{tenantData.hours}</p>
                  </div>
                </div>

                <div className="social-links">
                  <a href="#" className="social-link"><Facebook size={20} /></a>
                  <a href="#" className="social-link"><Instagram size={20} /></a>
                  <a href={`https://wa.me/${tenantData.whatsapp}`} className="social-link whatsapp">
                    <MessageCircle size={20} />
                  </a>
                </div>
              </div>

              <div className="contact-form-card">
                <h3>Send us a Message</h3>
                <form className="contact-form">
                  <div className="form-row">
                    <input type="text" placeholder="Your Name" />
                    <input type="email" placeholder="Your Email" />
                  </div>
                  <input type="text" placeholder="Subject" />
                  <textarea rows={5} placeholder="Your Message"></textarea>
                  <button type="submit" className="btn-primary">
                    Send Message <ArrowRight size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bolt-footer">
        <div className="bolt-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="bolt-logo">
                <div className="logo-icon">
                  <Zap size={18} />
                </div>
                <span>{tenantData.companyName}</span>
              </div>
              <p>Premium logistics for the modern world. Fast, reliable, and transparent shipping from the USA & China to Jamaica.</p>
              <div className="footer-social">
                <a href="#"><Facebook size={18} /></a>
                <a href="#"><Instagram size={18} /></a>
                <a href="#"><MessageCircle size={18} /></a>
              </div>
            </div>
            
            <div className="footer-links">
              <h4>Support</h4>
              <button onClick={() => setActivePage('track')}>Track Package</button>
              <button onClick={() => setActivePage('rates')}>Rate Calculator</button>
              <button onClick={() => setActivePage('contact')}>Contact Us</button>
            </div>
            
            <div className="footer-links">
              <h4>Services</h4>
              <button onClick={() => setActivePage('services')}>Air Freight</button>
              <button onClick={() => setActivePage('services')}>Ocean Freight</button>
              <button onClick={() => setActivePage('services')}>Customs Brokerage</button>
            </div>
            
            <div className="footer-contact">
              <h4>Contact</h4>
              <p><Phone size={14} /> {tenantData.phone}</p>
              <p><Mail size={14} /> {tenantData.email}</p>
              <p><MapPin size={14} /> {tenantData.address}</p>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; 2025 {tenantData.companyName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
